import { QuickFilterBar as FilterBar } from "components/form_components/QuickFilterBar";
import { EventDataTypes } from "lib/tracking";
import { useOrganisations } from "../../lib/hooks/api/organisations/useOrganisations";
import { FilterFormProps, FilterSections } from "./FilterForm";
import {
  convertSupplierFiltersToOrganisationParams,
  getSupplierColumnCopy,
  SupplierFilters,
} from "./utils";

const sectionsToFilters: Record<FilterSections, (keyof SupplierFilters)[]> = {
  suppliers: ["suppliers", "isSme"],
  supplierGuids: ["guids"],
  buyers: ["buyerIds", "buyerLists", "buyerCategories"],
  lots: ["lotIds"],
  signals: [],
};

function getAppliedFilters(
  filters: SupplierFilters,
  hiddenFilterSections?: FilterFormProps["hiddenSections"],
) {
  const appliedFilters = Object.entries(filters).filter(([key, value]) => {
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    if (typeof value === "object") {
      if (key === "sort") {
        return false;
      }
      return Object.keys(value).length > 0;
    }
    if (typeof value === "string") {
      return value.length > 0;
    }
    if (typeof value === "boolean") {
      return true;
    }
    return false;
  });

  const hiddenFilters =
    hiddenFilterSections?.flatMap((section) => sectionsToFilters[section]) || [];

  return appliedFilters
    .map(([key]) => key as keyof typeof filters)
    .filter((key) => !hiddenFilters.includes(key));
}

const filterKeyToLabel: Record<keyof SupplierFilters, string> = {
  isSme: "SME Supplier",
  buyerIds: "Buyers",
  buyerLists: "Buyer lists",
  buyerCategories: "Buyer type",
  text: "",
  suppliers: "Suppliers",
  sort: "",
  guids: "Suppliers",
  lotIds: "Lots",
};

type Props = {
  filters: SupplierFilters;
  pageSize: number;
  currentPage: number;
  onClearFilter: (value: keyof SupplierFilters) => void;
  onFilterClick: (value: keyof SupplierFilters) => void;
  hiddenFilterSections?: FilterFormProps["hiddenSections"];
  clearAllFilters: () => void;
  clearAllFiltersHide?: () => boolean;
};
export function QuickFilterBar({
  filters,
  pageSize,
  currentPage,
  onClearFilter,
  onFilterClick,
  hiddenFilterSections,
  clearAllFilters,
  clearAllFiltersHide,
}: Props) {
  const appliedFilters = getAppliedFilters(filters, hiddenFilterSections);

  const { data, isLoading } = useOrganisations(
    convertSupplierFiltersToOrganisationParams(filters, pageSize, currentPage),
  );

  return (
    <FilterBar<typeof filters>
      appliedFilters={appliedFilters}
      filterKeyToLabel={filterKeyToLabel}
      sortField={getSupplierColumnCopy(filters.sort?.field || "name")}
      onClear={onClearFilter}
      isLoading={isLoading}
      totalResults={data?.searchOrganisations.total ?? 0}
      hiddenFields={["text"]}
      dataType={EventDataTypes.supplier}
      onFilterClick={onFilterClick}
      clearAllFilters={clearAllFilters}
      clearAllFiltersHide={clearAllFiltersHide}
    />
  );
}
